import { UseQueryOptions } from '@tanstack/react-query';
import {
	ScopeType,
	VariableConfig,
	scopedClientKey,
	scopedProjectKey,
	urlCombine,
} from 'ihub/common';
import { CRUDHelperBase } from '../classes/crud-helper-base';
import {
	buildURL,
	coreAPI,
	getTenantID,
	handleResponse,
	isURLSet,
	standardGet,
	standardPost,
	validateOkResponse,
} from '../classes/http-helper-const';
import Parameter from '../classes/query-parameter';
import { cacheStaleTime } from '../constants';
import HttpResponseOptions from '../interface/http-response-options';
import QueryClientWrapper from '../utils/create-query-client';

export class VariableHttpService extends CRUDHelperBase<VariableConfig> {
	constructor() {
		super(VariableConfig.name);
	}

	protected readonly getScopedVariablesPath = 'GetScopedVariables';
	protected readonly getByProjectScopeIDPath = 'GetByProjectScopeId';
	protected readonly getByClientScopeIDPath = 'GetByClientScopeId';
	protected readonly getScopedClientVariablesPath = 'GetScopedClientVariables';
	protected readonly GetScopedProjectVariablesPath = 'GetScopedProjectVariables';
	protected readonly SaveAllForProjectPath = 'SaveAllForProject';
	protected readonly SaveAllForClientPath = 'SaveAllForClient';

	getByScopeId(
		scopeID: string,
		scopeType: ScopeType
	): UseQueryOptions<VariableConfig[], Error, VariableConfig[]> {
		const isClient = scopeType === ScopeType.Client;

		const headers: Record<string, string> = {
			[isClient ? scopedClientKey : scopedProjectKey]: scopeID,
		};
		const path = isClient ? this.getByClientScopeIDPath : this.getByProjectScopeIDPath;

		return {
			queryKey: [getTenantID(), this.objectType, path, scopeID],
			queryFn: async (): Promise<VariableConfig[]> => {
				isURLSet();
				const url = urlCombine(coreAPI, this.objectType, path, scopeID);
				const res = await standardGet(url, headers);
				validateOkResponse(res, 'get by scope ID', this.objectType);
				return await res.clone().json();
			},
			staleTime: cacheStaleTime,
		};
	}

	async saveAllScoped(
		scopeID: string,
		scopeType: ScopeType,
		items: VariableConfig[],
		options?: HttpResponseOptions,
		queryParams?: Parameter[],
		shouldInvalidateKeys?: boolean
	): Promise<void> {
		const isClient = scopeType === ScopeType.Client;
		const headers: Record<string, string> = {
			[isClient ? scopedClientKey : scopedProjectKey]: scopeID,
		};
		const path = isClient ? this.SaveAllForClientPath : this.SaveAllForProjectPath;

		const url = buildURL(this.objectType, [path], queryParams);
		const response = await standardPost(url, items, headers);
		await handleResponse(response, 'save all', this.objectType, options);
		if (shouldInvalidateKeys) {
			await this.invalidateKeys();
		}
	}

	getScopedVariables(
		queryParams?: Parameter[]
	): UseQueryOptions<VariableConfig[], Error, VariableConfig[]> {
		return {
			queryKey: [
				getTenantID(),
				this.objectType,
				this.getScopedVariablesPath,
				queryParams?.map(x => `${x.key}=${x.value}`).join('&'),
			],
			queryFn: async (): Promise<VariableConfig[]> => {
				isURLSet();
				let url = urlCombine(coreAPI, this.objectType, this.getScopedVariablesPath);
				if (queryParams && queryParams.length > 0) {
					url += `?` + queryParams.map(x => `${x.key}=${x.value}`).join('&');
				}
				const res = await standardGet(url);
				validateOkResponse(res, 'get scope variables', this.objectType);
				return await res.clone().json();
			},
			staleTime: cacheStaleTime,
		};
	}

	async invalidateGetScopedVariables(queryParams?: Parameter[], remove = false) {
		await QueryClientWrapper.InvalidateKey(
			[
				getTenantID(),
				this.objectType,
				this.getScopedVariablesPath,
				queryParams?.map(x => `${x.key}=${x.value}`).join('&'),
			],
			remove
		);
		await QueryClientWrapper.InvalidateKey(
			[
				getTenantID(),
				this.objectType,
				this.getScopedVariablesPath,
				queryParams?.map(x => `${x.key}=${x.value}`).join('&'),
			],
			remove
		);
		await QueryClientWrapper.InvalidateKey(
			[
				getTenantID(),
				this.objectType,
				this.getScopedClientVariablesPath,
				queryParams?.map(x => `${x.key}=${x.value}`).join('&'),
			],
			remove
		);
	}
}
